import { z } from 'zod';

import {
  BaseDeckManagementEntry,
  createChangeSetOperations,
  nullableNumber,
  nullableNumberArray,
  nullableString,
} from './Common.schema';

/**
 * Schema for DeckColumn entries.
 * Extends BaseDeckManagementEntry to ensure consistent pk/sk handling.
 * Base schema for DeckColumn that matches the API type structure.
 */
export const DeckColumnSchema = BaseDeckManagementEntry.extend({
  // TODO: Currently using defined basic types. Change these into more specific validations in the future.
  page_id: z.string(),
  deckcolumn_id: z.string(),
  column_template: nullableString,
  page_section: nullableString,
  column_no: nullableNumber,
  column_title: nullableString,
  column_name: nullableString,
  no_of_columns: nullableNumber,
  column_description: nullableString,
  scenario_display_name: nullableString,
  scenario_period: nullableString,
  block_id: nullableNumber,
  footnotes: nullableNumberArray,
  comparison_scenario: nullableString,
  comparison_scenario_period: nullableString,
  fx_scenario: nullableString,
  fx_scenario_period: nullableString,
  denominator_scenario: nullableString,
  denominator_scenario_period: nullableString,
  block_header: nullableString,
  non_material_flag: nullableString,
  denominator_kpi_exception_flag: nullableString,
  column_mapping_group: nullableString,
  report_header: nullableString,
  time_grain: nullableString,
});

/**
 * Schema for creating new DeckColumn entries.
 * Omits pk and sk fields as these are generated by the service.
 * Matches DeckColumnItemsInput GraphQL type requirements.
 */
export const CreateDeckColumnSchema = DeckColumnSchema.omit({
  pk: true,
  sk: true,
});

/**
 * Schema for updating existing DeckColumn entries.
 * Omits pk and sk fields as these are generated by the service.
 * Matches DeckColumnItemsInput GraphQL type requirements.
 */
export const UpdateDeckColumnSchema = DeckColumnSchema.omit({
  pk: true,
  sk: true,
});

/**
 * Schema for deleting existing DeckColumn entries
 * Includes only the primary fields needed for DeckColumnDeleteItemsInput
 */
export const DeleteDeckColumnSchema = z.object({
  page_id: z.string(),
  deckcolumn_id: z.string(),
});

/**
 * Complete changeset schema for DeckColumn operations.
 * Provides create, update, and delete arrays with appropriate validation.
 */
export const DeckColumnChangeSetSchema = createChangeSetOperations({
  create: CreateDeckColumnSchema,
  update: UpdateDeckColumnSchema,
  delete: DeleteDeckColumnSchema,
});

/**
 * Type definitions for use in components and services
 */

/**
 * Type for DeckColumn entries in create operations (without pk/sk)
 */
export type CreateDeckColumnType = z.infer<typeof CreateDeckColumnSchema>;

/**
 * Type for DeckColumn entries in udpate operation (without pk/sk, or last_updated_date)
 */
export type UpdateDeckColumnType = z.infer<typeof UpdateDeckColumnSchema>;

/**
 * Type for DeckColumn entries in delete operations (with all fields)
 */
export type DeleteDeckColumnType = z.infer<typeof DeleteDeckColumnSchema>;

/**
 * Type for the complete DeckColumn changeset structure
 */
export type DeckColumnChangeSetType = z.infer<typeof DeckColumnChangeSetSchema>;
